import { Percentage } from "@dreamhub/designsystem/icons";
import { ScoreType } from "@/pages/deals/scoreConfig";
import IconContainer from "@/components/ui/icon-container";
import Input from "@/components/ui/input";
import { DealScoreBadge } from "@/pages/deals/components/DealScoreBadge";
import Text from "@/components/ui/text";
import React from "react";
import { useTranslation } from "react-i18next";

const TRANSLATION_PREFIX =
  "settings.deals.workflow.workflowDetailedView.scoreGuardrails";

interface Props {
  kpiValue: ScoreType;
  scoreText: string;
  disabledInputValue: number;
  fieldsNumRange: number[];
  inputValue?: string;
  isLoadingThreshold?: boolean;
  isUpdating?: boolean;
  handleInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputBlur?: () => void;
  subText?: string;
  secondSubText?: string;
  onForceUpdate?: (value: number) => void;
}

const ScoreGuardrailRow: React.FC<Props> = ({
  kpiValue,
  scoreText,
  disabledInputValue,
  fieldsNumRange,
  inputValue,
  isLoadingThreshold = false,
  isUpdating = false,
  handleInputChange,
  handleInputBlur,
  subText,
  secondSubText,
  onForceUpdate
}) => {
  const { t } = useTranslation();

  const handleValidatedInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === "") {
      handleInputChange?.(e);
      return;
    }

    // Allow any numeric input during typing (including partial numbers)
    if (/^\d*\.?\d*$/.test(value)) {
      handleInputChange?.(e);
    }
  };

  const handleValidatedInputBlur = () => {
    if (inputValue) {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue) && numericValue < disabledInputValue) {
        return onForceUpdate?.(disabledInputValue + 1);
      }
    }
    handleInputBlur?.();
  };

  return (
    <div className="p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between w-full">
        <div className="flex gap-4 items-center">
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              disabled
              value={disabledInputValue.toString()}
              className="w-40 border-border-field-default"
              leadingIcon={
                <IconContainer size="5" color="tertiary">
                  <Percentage />
                </IconContainer>
              }
              placeholder={t(
                "settings.deals.fieldManagement.fieldDisplayTypes.percentage"
              )}
              min={0}
            />
            <Text variant="bodySecondary">-</Text>
            <Input
              type="number"
              value={inputValue}
              disabled={isLoadingThreshold || isUpdating}
              onChange={handleValidatedInputChange}
              onBlur={handleValidatedInputBlur}
              className="w-40 border-border-field-default"
              leadingIcon={
                <IconContainer size="5" color="tertiary">
                  <Percentage />
                </IconContainer>
              }
              placeholder={t(
                "settings.deals.fieldManagement.fieldDisplayTypes.percentage"
              )}
              min={disabledInputValue}
              maxLength={3}
            />
          </div>
          <Text variant="bodySecondary">
            {t(`${TRANSLATION_PREFIX}.fields`, {
              firstNum: fieldsNumRange[0],
              secondNum: fieldsNumRange[1]
            })}
          </Text>
        </div>
        <DealScoreBadge
          scoreText={scoreText}
          scoreType={kpiValue}
          className="w-40"
        />
      </div>
      {subText && <Text>{subText}</Text>}
      {secondSubText && <Text>{secondSubText}</Text>}
    </div>
  );
};

export default ScoreGuardrailRow;
